/**
 * /app/crm — agency CRM pipeline (Task 041 §2).
 *
 * Wired against `list-crm-leads`, `get-crm-lead`, `list-crm-deals`,
 * `get-crm-deal`, `list-crm-deal-activities`,
 * `list-crm-deal-stage-history`. CRM is org-wide at the wrapper level
 * — no `client_id` parameter on any of these endpoints — so the agency
 * page exposes the same data the existing client tab does, without the
 * "this clientId is just sidebar context" disclaimer banner.
 *
 * Mutation surfaces (lead/deal create / update / convert / activity
 * create / stage transition) are deferred — action buttons render
 * `.btn-coming-soon`.
 */

import { useEffect, useState } from 'react';
import { isApiError } from '../api/client';
import {
  getCrmDeal,
  getCrmLead,
  listCrmDealActivities,
  listCrmDealStageHistory,
  listCrmDeals,
  listCrmLeads,
} from '../api/endpoints';
import type {
  CrmDealActivityRow,
  CrmDealActivityType,
  CrmDealDetailRow,
  CrmDealListRow,
  CrmDealStage,
  CrmDealStageHistoryRow,
  CrmLeadDetailRow,
  CrmLeadListRow,
  CrmLeadStatus,
} from '../api/types';
import { EmptyState, ErrorState, LoadingState, NotFoundState } from '../components/states';

type Tab = 'leads' | 'deals';

type LeadsListState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly leads: ReadonlyArray<CrmLeadListRow> }
  | { readonly status: 'error'; readonly error: unknown };

type DealsListState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly deals: ReadonlyArray<CrmDealListRow> }
  | { readonly status: 'error'; readonly error: unknown };

type LeadDetailState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly lead: CrmLeadDetailRow }
  | { readonly status: 'not-found' }
  | { readonly status: 'error'; readonly error: unknown };

type DealDetailState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly deal: CrmDealDetailRow;
      readonly activities: ReadonlyArray<CrmDealActivityRow>;
      readonly history: ReadonlyArray<CrmDealStageHistoryRow>;
    }
  | { readonly status: 'not-found' }
  | { readonly status: 'error'; readonly error: unknown };

const LEAD_STATUS_FILTERS: ReadonlyArray<{
  readonly value: CrmLeadStatus | 'all';
  readonly label: string;
}> = [
  { value: 'all', label: 'الكل' },
  { value: 'new', label: 'جديد' },
  { value: 'contacted', label: 'تم التواصل' },
  { value: 'qualified', label: 'مؤهل' },
  { value: 'unqualified', label: 'غير مؤهل' },
  { value: 'converted', label: 'محوّل' },
];

const DEAL_STAGE_FILTERS: ReadonlyArray<{
  readonly value: CrmDealStage | 'all';
  readonly label: string;
}> = [
  { value: 'all', label: 'الكل' },
  { value: 'discovery', label: 'استكشاف' },
  { value: 'proposal', label: 'عرض' },
  { value: 'negotiation', label: 'تفاوض' },
  { value: 'won', label: 'مكسبة' },
  { value: 'lost', label: 'مفقودة' },
];

export function AgencyCrmPage() {
  const [tab, setTab] = useState<Tab>('leads');

  return (
    <div className="stack-md">
      <div>
        <h1 className="h-page">
          قمع <em className="serif italic">المبيعات</em>
        </h1>
        <p className="body" style={{ marginTop: 6, color: 'var(--ink-2)' }}>
          العملاء المحتملون والصفقات على مستوى الوكالة — قراءة فقط في MVP.
        </p>
      </div>

      <div className="tabs" role="tablist">
        <button
          type="button"
          role="tab"
          className={tab === 'leads' ? 'tab active' : 'tab'}
          onClick={() => setTab('leads')}
        >
          العملاء المحتملون
        </button>
        <button
          type="button"
          role="tab"
          className={tab === 'deals' ? 'tab active' : 'tab'}
          onClick={() => setTab('deals')}
        >
          الصفقات
        </button>
      </div>

      {tab === 'leads' ? <LeadsTab /> : <DealsTab />}
    </div>
  );
}

function LeadsTab() {
  const [statusFilter, setStatusFilter] = useState<CrmLeadStatus | 'all'>('all');
  const [list, setList] = useState<LeadsListState>({ status: 'loading' });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<LeadDetailState>({ status: 'idle' });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const ctrl = new AbortController();
    setList({ status: 'loading' });
    listCrmLeads({
      status: statusFilter === 'all' ? undefined : statusFilter,
      limit: 50,
      signal: ctrl.signal,
    })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        setList({ status: 'ready', leads: data.leads });
        if (data.leads.length > 0 && data.leads[0]) {
          setSelectedId((prev) =>
            prev && data.leads.some((l) => l.id === prev) ? prev : data.leads[0]!.id,
          );
        } else {
          setSelectedId(null);
        }
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setList({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [statusFilter, reloadKey]);

  useEffect(() => {
    if (!selectedId) {
      setDetail({ status: 'idle' });
      return;
    }
    const ctrl = new AbortController();
    setDetail({ status: 'loading' });
    getCrmLead(selectedId, { signal: ctrl.signal })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        setDetail({ status: 'ready', lead: data.lead });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (isApiError(err) && err.code === 'not_found') {
          setDetail({ status: 'not-found' });
          return;
        }
        setDetail({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [selectedId]);

  return (
    <div className="stack-md">
      <div className="toolbar-row">
        {LEAD_STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            className={statusFilter === f.value ? 'chip on' : 'chip'}
            onClick={() => setStatusFilter(f.value)}
          >
            {f.label}
          </button>
        ))}
        <span style={{ marginInlineStart: 'auto' }}>
          <button type="button" className="btn-coming-soon" disabled>
            عميل محتمل جديد (قريباً)
          </button>
        </span>
      </div>

      {list.status === 'loading' ? <LoadingState title="جاري تحميل العملاء المحتملين…" /> : null}
      {list.status === 'error' ? (
        <ErrorState
          title="تعذر تحميل العملاء المحتملين"
          error={list.error}
          onRetry={() => setReloadKey((n) => n + 1)}
        />
      ) : null}
      {list.status === 'ready' ? (
        list.leads.length === 0 ? (
          <EmptyState
            title="لا يوجد عملاء محتملون مطابقون"
            description="جرّب فلتر آخر، أو انتظر إضافة أول عميل محتمل."
          />
        ) : (
          <div className="workspace-split">
            <aside className="workspace-list">
              <div className="sec-title">
                <h2>العملاء المحتملون</h2>
                <span className="meta">{list.leads.length}</span>
              </div>
              <ul className="row-list">
                {list.leads.map((l) => (
                  <li key={l.id}>
                    <button
                      type="button"
                      className={l.id === selectedId ? 'row-card row-card-active' : 'row-card'}
                      onClick={() => setSelectedId(l.id)}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                        <strong style={{ fontSize: 13 }}>{l.company_name}</strong>
                        <span className={`pill pill-${leadStatusVariant(l.status)}`}>
                          {leadStatusLabel(l.status)}
                        </span>
                      </div>
                      <div className="tiny" style={{ marginTop: 6, color: 'var(--muted)' }}>
                        {l.contact_name ?? 'بدون اسم اتصال'}
                        {l.estimated_value
                          ? ` · ${formatCurrency(l.estimated_value, l.currency_code)}`
                          : ''}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </aside>
            <section className="workspace-detail">
              <LeadDetail state={detail} />
            </section>
          </div>
        )
      ) : null}
    </div>
  );
}

function DealsTab() {
  const [stageFilter, setStageFilter] = useState<CrmDealStage | 'all'>('all');
  const [list, setList] = useState<DealsListState>({ status: 'loading' });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DealDetailState>({ status: 'idle' });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const ctrl = new AbortController();
    setList({ status: 'loading' });
    listCrmDeals({
      stage: stageFilter === 'all' ? undefined : stageFilter,
      limit: 50,
      signal: ctrl.signal,
    })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        setList({ status: 'ready', deals: data.deals });
        if (data.deals.length > 0 && data.deals[0]) {
          setSelectedId((prev) =>
            prev && data.deals.some((d) => d.id === prev) ? prev : data.deals[0]!.id,
          );
        } else {
          setSelectedId(null);
        }
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setList({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [stageFilter, reloadKey]);

  useEffect(() => {
    if (!selectedId) {
      setDetail({ status: 'idle' });
      return;
    }
    const ctrl = new AbortController();
    setDetail({ status: 'loading' });
    Promise.all([
      getCrmDeal(selectedId, { signal: ctrl.signal }),
      listCrmDealActivities(selectedId, { signal: ctrl.signal }),
      listCrmDealStageHistory(selectedId, { signal: ctrl.signal }),
    ])
      .then(([dealData, activitiesData, historyData]) => {
        if (ctrl.signal.aborted) return;
        setDetail({
          status: 'ready',
          deal: dealData.deal,
          activities: activitiesData.deal_activities,
          history: historyData.deal_stage_history,
        });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (isApiError(err) && err.code === 'not_found') {
          setDetail({ status: 'not-found' });
          return;
        }
        setDetail({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [selectedId]);

  return (
    <div className="stack-md">
      <div className="toolbar-row">
        {DEAL_STAGE_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            className={stageFilter === f.value ? 'chip on' : 'chip'}
            onClick={() => setStageFilter(f.value)}
          >
            {f.label}
          </button>
        ))}
        <span style={{ marginInlineStart: 'auto' }}>
          <button type="button" className="btn-coming-soon" disabled>
            صفقة جديدة (قريباً)
          </button>
        </span>
      </div>

      {list.status === 'loading' ? <LoadingState title="جاري تحميل الصفقات…" /> : null}
      {list.status === 'error' ? (
        <ErrorState
          title="تعذر تحميل الصفقات"
          error={list.error}
          onRetry={() => setReloadKey((n) => n + 1)}
        />
      ) : null}
      {list.status === 'ready' ? (
        list.deals.length === 0 ? (
          <EmptyState
            title="لا توجد صفقات مطابقة"
            description="جرّب فلتر مرحلة أخرى، أو انتظر إضافة أول صفقة."
          />
        ) : (
          <div className="workspace-split">
            <aside className="workspace-list">
              <div className="sec-title">
                <h2>الصفقات</h2>
                <span className="meta">{list.deals.length}</span>
              </div>
              <ul className="row-list">
                {list.deals.map((d) => (
                  <li key={d.id}>
                    <button
                      type="button"
                      className={d.id === selectedId ? 'row-card row-card-active' : 'row-card'}
                      onClick={() => setSelectedId(d.id)}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                        <strong style={{ fontSize: 13 }}>{d.title}</strong>
                        <span className={`pill pill-${dealStageVariant(d.stage)}`}>
                          {dealStageLabel(d.stage)}
                        </span>
                      </div>
                      <div className="tiny" style={{ marginTop: 6, color: 'var(--muted)' }}>
                        {d.expected_value
                          ? formatCurrency(d.expected_value, d.currency_code)
                          : 'بدون قيمة متوقعة'}
                        {d.expected_close_date
                          ? ` · ${new Date(d.expected_close_date).toLocaleDateString('ar-SA')}`
                          : ''}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </aside>
            <section className="workspace-detail">
              <DealDetail state={detail} />
            </section>
          </div>
        )
      ) : null}
    </div>
  );
}

function LeadDetail({ state }: { readonly state: LeadDetailState }) {
  if (state.status === 'idle')
    return (
      <EmptyState
        title="اختر عميلاً محتملاً لعرض التفاصيل"
        description="اضغط أي بطاقة من القائمة على اليمين."
      />
    );
  if (state.status === 'loading') return <LoadingState title="جاري تحميل التفاصيل…" />;
  if (state.status === 'not-found')
    return <NotFoundState title="العميل المحتمل غير متاح" description="ربما تم أرشفته." />;
  if (state.status === 'error')
    return <ErrorState title="تعذر تحميل التفاصيل" error={state.error} />;

  const { lead } = state;
  return (
    <div className="card" style={{ padding: 22 }}>
      <div className="sec-title">
        <h2>{lead.company_name}</h2>
        <span className={`pill pill-${leadStatusVariant(lead.status)}`}>
          {leadStatusLabel(lead.status)}
        </span>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))',
          gap: 14,
        }}
      >
        <Field label="اسم الاتصال" value={lead.contact_name} />
        <Field label="البريد" value={lead.contact_email} />
        <Field label="الهاتف" value={lead.contact_phone} />
        <Field label="المصدر" value={lead.source} />
        <Field
          label="القيمة المقدرة"
          value={
            lead.estimated_value ? formatCurrency(lead.estimated_value, lead.currency_code) : null
          }
        />
        <Field label="تم الإنشاء" value={new Date(lead.created_at).toLocaleString('ar-SA')} />
      </div>
      {lead.notes ? (
        <p
          className="body"
          style={{ marginTop: 14, whiteSpace: 'pre-wrap', color: 'var(--ink-2)' }}
        >
          {lead.notes}
        </p>
      ) : null}
      <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
        <button type="button" className="btn-coming-soon" disabled>
          تحديث الحالة (قريباً)
        </button>
        <button type="button" className="btn-coming-soon" disabled>
          تحويل لصفقة (قريباً)
        </button>
      </div>
    </div>
  );
}

function DealDetail({ state }: { readonly state: DealDetailState }) {
  if (state.status === 'idle')
    return (
      <EmptyState
        title="اختر صفقة لعرض التفاصيل"
        description="اضغط أي بطاقة من القائمة على اليمين."
      />
    );
  if (state.status === 'loading') return <LoadingState title="جاري تحميل تفاصيل الصفقة…" />;
  if (state.status === 'not-found')
    return <NotFoundState title="الصفقة غير متاحة" description="ربما تم أرشفتها." />;
  if (state.status === 'error')
    return <ErrorState title="تعذر تحميل تفاصيل الصفقة" error={state.error} />;

  const { deal, activities, history } = state;
  return (
    <div className="stack-md">
      <div className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <h2>{deal.title}</h2>
          <span className={`pill pill-${dealStageVariant(deal.stage)}`}>
            {dealStageLabel(deal.stage)}
          </span>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
            gap: 14,
          }}
        >
          <Field
            label="القيمة المتوقعة"
            value={
              deal.expected_value ? formatCurrency(deal.expected_value, deal.currency_code) : null
            }
          />
          <Field
            label="الاحتمالية"
            value={deal.probability !== null ? `${(deal.probability * 100).toFixed(0)}%` : null}
          />
          <Field
            label="تاريخ الإغلاق المتوقع"
            value={
              deal.expected_close_date
                ? new Date(deal.expected_close_date).toLocaleDateString('ar-SA')
                : null
            }
          />
          <Field
            label="تم الكسب"
            value={deal.won_at ? new Date(deal.won_at).toLocaleDateString('ar-SA') : null}
          />
          <Field
            label="تم الفقدان"
            value={deal.lost_at ? new Date(deal.lost_at).toLocaleDateString('ar-SA') : null}
          />
          <Field label="سبب الفقدان" value={deal.lost_reason} />
        </div>
        <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
          <button type="button" className="btn-coming-soon" disabled>
            تحديث المرحلة (قريباً)
          </button>
          <button type="button" className="btn-coming-soon" disabled>
            تحويل لعميل (قريباً)
          </button>
        </div>
      </div>

      <div className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <h2>الأنشطة</h2>
          <span className="meta">{activities.length}</span>
        </div>
        {activities.length === 0 ? (
          <EmptyState title="لا توجد أنشطة" description="ستظهر هنا الأنشطة المسجّلة على الصفقة." />
        ) : (
          <ul className="row-list">
            {activities.map((a) => (
              <li key={a.id} className="row-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <strong>{a.subject}</strong>
                  <span className="pill pill-info">{activityTypeLabel(a.activity_type)}</span>
                </div>
                {a.body ? (
                  <p className="body" style={{ marginTop: 6 }}>
                    {a.body}
                  </p>
                ) : null}
                <div className="tiny" style={{ color: 'var(--muted)', marginTop: 6 }}>
                  {new Date(a.occurred_at).toLocaleString('ar-SA')}
                  {a.completed_at
                    ? ` · مكتمل ${new Date(a.completed_at).toLocaleDateString('ar-SA')}`
                    : a.due_at
                      ? ` · مستحق ${new Date(a.due_at).toLocaleDateString('ar-SA')}`
                      : ''}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <h2>سجلّ المراحل</h2>
          <span className="meta">{history.length}</span>
        </div>
        {history.length === 0 ? (
          <EmptyState
            title="لا توجد تحوّلات مرحلة"
            description="ستظهر هنا التحوّلات بين المراحل عند تسجيلها."
          />
        ) : (
          <ul className="row-list">
            {history.map((h) => (
              <li key={h.id} className="row-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <span>
                    {h.from_stage ? (
                      <>
                        <span className={`pill pill-${dealStageVariant(h.from_stage)}`}>
                          {dealStageLabel(h.from_stage)}
                        </span>
                        <span style={{ margin: '0 6px', color: 'var(--muted)' }}>→</span>
                      </>
                    ) : null}
                    <span className={`pill pill-${dealStageVariant(h.to_stage)}`}>
                      {dealStageLabel(h.to_stage)}
                    </span>
                  </span>
                  <span className="tiny" style={{ color: 'var(--muted)' }}>
                    {new Date(h.changed_at).toLocaleString('ar-SA')}
                  </span>
                </div>
                {h.reason ? (
                  <p className="body" style={{ marginTop: 6 }}>
                    {h.reason}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function leadStatusLabel(status: CrmLeadStatus): string {
  switch (status) {
    case 'new':
      return 'جديد';
    case 'contacted':
      return 'تم التواصل';
    case 'qualified':
      return 'مؤهل';
    case 'unqualified':
      return 'غير مؤهل';
    case 'converted':
      return 'محوّل';
    case 'archived':
      return 'مؤرشف';
    default:
      return status;
  }
}

function leadStatusVariant(status: CrmLeadStatus): string {
  switch (status) {
    case 'new':
      return 'info';
    case 'contacted':
      return 'review';
    case 'qualified':
      return 'active';
    case 'unqualified':
      return 'archived';
    case 'converted':
      return 'active';
    case 'archived':
      return 'archived';
    default:
      return 'info';
  }
}

function dealStageLabel(stage: CrmDealStage): string {
  switch (stage) {
    case 'discovery':
      return 'استكشاف';
    case 'proposal':
      return 'عرض';
    case 'negotiation':
      return 'تفاوض';
    case 'won':
      return 'مكسبة';
    case 'lost':
      return 'مفقودة';
    default:
      return stage;
  }
}

function dealStageVariant(stage: CrmDealStage): string {
  switch (stage) {
    case 'discovery':
      return 'draft';
    case 'proposal':
      return 'info';
    case 'negotiation':
      return 'review';
    case 'won':
      return 'active';
    case 'lost':
      return 'error';
    default:
      return 'info';
  }
}

function activityTypeLabel(type: CrmDealActivityType): string {
  switch (type) {
    case 'note':
      return 'ملاحظة';
    case 'call':
      return 'مكالمة';
    case 'email':
      return 'إيميل';
    case 'meeting':
      return 'اجتماع';
    case 'proposal_sent':
      return 'إرسال عرض';
    case 'follow_up':
      return 'متابعة';
    case 'other':
      return 'أخرى';
    default:
      return type;
  }
}

function formatCurrency(value: number | string, currency: string | null | undefined): string {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  const code = currency && currency.length > 0 ? currency : 'SAR';
  try {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${n.toLocaleString('ar-SA')} ${code}`;
  }
}

function Field({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string | null | undefined;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="lbl">{label}</span>
      <span className="body">{value && value.length > 0 ? value : '—'}</span>
    </div>
  );
}
